<div class="GeneratorPage page page-entering" on:touchmove="handleSeed(event)" on:mousemove="handleSeed(event)">
  <div class="instructions {{(hasStarted) ? '' : 'active'}}">
    <div class="container">
      <h1 class="title">{{$dictionary.generator.title}}</h1>
      <h2 class="subtitle">{{$dictionary.generator.subtitle1}}</h2>
      <h2 class="subtitle">{{{$dictionary.generator.subtitle2}}}</h2>
      <Icon name="hand" />
    </div>
  </div>
  <div class="generator" ref:generator>
    <div class="progress-label {{(seedPercentage > 44) ? 'white' : ''}}">
      <h1 class="title">{{$dictionary.generator.keepSwiping}}</h1>
      <div class="percentage">{{Math.floor(seedPercentage)}}%</div>
    </div>
    <div class="progress" style="transform: translate3d(0, {{100 - seedPercentage}}%, 0)">
      <div class="seed no-selectable">{{obfuscatedPartialSeed}}</div>
    </div>
  </div>
</div>

<script>
  import roadtrip from 'roadtrip';
  import QRious from 'qrious';
  import BigInteger from '../../helpers/cicoin/BigInteger';
  import Cicoin from '../../helpers/cicoin/Cicoin';
  import Base58 from '../../helpers/cicoin/Base58';
  import ECurve from '../../helpers/cicoin/ECurve';
  import Icon from '../../components/icon/icon.component.svelte';
  import { isCompatible } from '../../helpers/checkCompatibility';

  export default {
    oncreate() {
      this.set({
        isTouchDevice: this.isTouchDevice()
      });
    },
    data() {
      return {
        lastTouch: +new Date(),
        touches: 0,
        totalTouches: 120 + (Math.round(crypto.getRandomValues(new Uint8Array(10))[5] / 4)),
        hasStarted: false,
        hasFinished: false,
        isTouchDevice: true,
        seedPercentage: 0,
        touchList: [],
        initialTouchTime: 0,
        finalTouchTime: 0,
        // This is used only for visual purposes and don't play any role
        // in final wallet security
        obfuscatedPartialSeed: '',
      }
    },
    methods: {
      goto(path, e) {
        if (e) {
          e.preventDefault();
        }
        roadtrip.goto(path);
      },
      isTouchDevice() {
        return 'ontouchstart' in window || window.innerWidth <= 1024;
      },
      handleSeed(e) {
        const isTouch = this.get('isTouchDevice');
        const touches = +this.get('touches');
        const totalTouches = +this.get('totalTouches');
        const numberOfWallets = window.store.get('numberOfWallets');

        // Ignore mouse events if device is mobile/touch
        if (isTouch && e.type === 'mousemove') {
          return;
        }

        // Ignore touch events if device is desktop/mouse
        if (!isTouch && e.type === 'touchmove') {
          return;
        }

        // If mousemove, simulate touch to keep with the function
        if (e.type === 'mousemove') {
          e.touches = [];
          e.touches[0] = {
            pageX: e.layerX,
            pageY: e.layerY 
          };
        }

        // If the event fired before 80ms from last one,
        // simply ignore it
        if (+new Date() - this.get('lastTouch') < 80) {
          return;
        }

        // If the number of touches surpassed the maximum needed,
        // also ignore it and generate wallet
        if (touches >= totalTouches) {
          // If this is the last computed touch, save its time for seed usage
          if (touches == totalTouches) {
            this.set({ finalTouchTime: +new Date() });
          }
          // Finally, generate wallet using computed seed.
          for (let i = 0; i < numberOfWallets; i++) {
            this.generateWallet(
              this.get('initialTouchTime'),
              this.get('finalTouchTime'),
              this.get('touchList')
            );
          }

          this.goto('/wallet');
          return;
        }

        // If first seed point, save its time for seed usage
        if (touches === 0) {
          this.set({ initialTouchTime: +new Date() });
        }

        // If none of the above conditionals triggered, keep with
        // seed calculation  
        const touchList = this.get('touchList');
        const userPoint = e.touches[0].pageX * e.touches[0].pageY;

        touchList.push(userPoint);
        
        this.set({
          lastTouch: +new Date(),
          touches: touches + 1,
          hasStarted: true,
          seedPercentage: (touches + 1) * 100 / totalTouches,
          touchList: touchList,
          obfuscatedPartialSeed: Cicoin.Crypto.SHA256(touchList.join(''), null).repeat(10)
        });

        this.addVisualPoint(e.touches[0].pageX, e.touches[0].pageY);
      },
      addVisualPoint(x, y) {
        const point = document.createElement('div');

        point.classList.add('point');
        point.style.top = `${y}px`;
        point.style.left = `${x}px`;

        this.refs.generator.appendChild(point);
      },
      generateWallet(initialTime, finalTime, touchList) {
        const wallets = window.store.get('wallets') || [];
        const numberOfWallets = window.store.get('numberOfWallets') || [];

        if (wallets.length >= numberOfWallets) {
          return;
        }

        // Seed
        const randomSeed = Array.from(window.crypto.getRandomValues(new Uint8Array(2048 / 8))).join('');
        const secret = `${randomSeed}:${initialTime}${touchList.join('')}${finalTime}`;

        // Generating cicoin address/priv. key
        const secretHash = Cicoin.Crypto.SHA256("" + secret);
        let privateKeyBNP2TH = BigInteger.fromByteArrayUnsigned(Cicoin.Crypto.hexToBytes(secretHash));
        let pubKeyP2TH = ECurve.getPublicKey(privateKeyBNP2TH);
        let pub_keyP2TH = ((pubKeyP2TH.yParity === "even") ? "02" : "03") + pubKeyP2TH.x.toString();    
        let sha = Cicoin.Crypto.SHA256(Cicoin.Crypto.hexToBytes(pub_keyP2TH), null);
        let hash160 = Cicoin.Crypto.RIPEMD160(Cicoin.Crypto.hexToBytes(sha), null);
        let hashAndBytes = Cicoin.Crypto.hexToBytes(hash160);
        hashAndBytes.unshift(Cicoin.Address.networkVersion);//Cicoin Public Address lead Hex value 
        let versionAndRipe = Cicoin.Crypto.bytesToHex(hashAndBytes);
        let check = this.computeChecksum(versionAndRipe);
        let address = Base58.encode(Cicoin.Crypto.hexToBytes(versionAndRipe + check.checksum));
        
        let pkWIF = "57" + secretHash + "01"; //compressionflag
        let checkpr = this.computeChecksum(pkWIF);
        let addresspriv = Base58.encode(Cicoin.Crypto.hexToBytes(pkWIF + checkpr.checksum));

        // Send generated Address to new page
        window.store.set({
          wallets: wallets.concat({
            publicAddress: address,
            privateKey: addresspriv,
            privateQRCode: new QRious({value: addresspriv, size: 200 * 4, level: 'L'}).toDataURL(),
            publicQRCode: new QRious({value: address, size: 200 * 4, level: 'L'}).toDataURL(),
          })
        });
      },
      computeChecksum(hx) {
        var firstSHA = Cicoin.Crypto.SHA256(Cicoin.Crypto.hexToBytes(hx));
        var secondSHA = Cicoin.Crypto.SHA256(Cicoin.Crypto.hexToBytes(firstSHA));
        return {
          checksum: secondSHA.substr(0,8).toUpperCase(),
          nonChecksum: secondSHA.substr(8,secondSHA.length).toUpperCase()
        };
      }
    },
    components: {
      Icon
    }
  }
</script>
